import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { FieldValues, Path, UseFormReturn, useWatch } from "react-hook-form";

import localeCore from "../../core/locale.core";
import { useTranslation } from "../../core/translation.core";
import ChevronDownIcon from "../../icons/chevronDown.icon";
import { FancyCheckbox } from "../reuseables/form.comp";
import brandCore from "../../core/brand.core";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import { Brand } from "../../types/brand";

export default function BrandMultipleSelectorComp<T extends FieldValues>({
  methods,
  name,
  labelMaxLength = 30,
}: {
  methods: UseFormReturn<T, any>;
  name: Path<T>;
  labelMaxLength?: number;
}) {
  const [isOpened, setIsOpened] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  function onClick(e: MouseEvent) {
    // This is a hack to get the latest state
    if (!ref || !ref.current) return;
    const isOpened = (ref.current as any).getAttribute("data-state");

    if (!isOpened) return;

    const isWithin = (e.target as any).closest("#brand-select");

    if (!isWithin) {
      setIsOpened(false);
    }
  }

  const css =
    "relative " +
    "h-9 w-full " +
    "dark:bg-her-backblue " +
    `border rounded-t-md ${
      !isOpened ? "rounded-b-md" : ""
    } hover:border-blue-200 hover:border-2 focus-visible:border-blue-600 focus-visible:border-2 ` +
    "focus-visible:outline-none outline-none " +
    "focus-visible:shadow-sm focus-visible:shadow-blue-800 " +
    "duration-1000 transition-[box-shadow] " +
    "disabled:text-her-lightgray2 text-base ";

  return (
    <div id="brand-select" ref={ref} data-state={isOpened} className={css}>
      <button
        type="button"
        className="w-full h-full flex justify-between pr-2 pl-4 items-center"
        onClick={() => setIsOpened((value) => !value)}
      >
        <CurrentName methods={methods} name={name} labelMaxLength={labelMaxLength} /> <ChevronDownIcon />
      </button>
      {isOpened && <Panel methods={methods} name={name} />}
    </div>
  );
}

function CurrentName<T extends FieldValues>({
  methods,
  name,
  labelMaxLength,
}: {
  methods: UseFormReturn<T, any>;
  name: Path<T>;
  labelMaxLength: number;
}) {
  const brandIds = useWatch({ name, control: methods.control }) as string[];
  const { locale } = useRouter();
  const { msg, t } = useTranslation();
  const {
    data: brands,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ["brands"],
    queryFn: () => brandCore.listBrands(1, 99999),
  });

  if (isError) return <ErrorCenterIcon reason={error.message} />;
  if (isPending) return <LoadingOvalCenterIcon />;
  if (!brands) return null;

  if (!brandIds || brandIds.length === 0) return <div>{t(msg.brand.selectBrands)}</div>;

  const names = brandIds.map((brandId) => {
    const foundBrand = brands.items.find((c) => c.id === brandId);
    if (!foundBrand) return undefined;
    return localeCore.getLocaleValueFallback(foundBrand.name, locale!);
  });

  let text = names.join(", ");

  if (text.length > labelMaxLength) {
    text = text.substring(0, labelMaxLength - 3) + " ...";
  }

  return <div>{text}</div>;
}

function Panel<T extends FieldValues>({ methods, name }: { methods: UseFormReturn<T, any>; name: Path<T> }) {
  const {
    data: brands,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ["brands"],
    queryFn: () => brandCore.listBrands(1, 99999),
  });

  if (isError) return <ErrorCenterIcon reason={error.message} />;
  if (isPending) return <LoadingOvalCenterIcon />;
  if (!brands) return null;

  return (
    <div className="brand_panel absolute top-[34px] left-0 w-full border rounded-b-md hover:border-blue-200 bg-white dark:bg-her-backblue space-y-1 px-4 py-4 max-h-72 overflow-y-auto">
      {brands.items.map((c) => (
        <BrandRow key={c.id} brand={c} methods={methods} name={name} />
      ))}
    </div>
  );
}

function BrandRow<T extends FieldValues>({
  brand,
  methods,
  name,
}: {
  brand: Brand;
  methods: UseFormReturn<T, any>;
  name: Path<T>;
}) {
  const { locale } = useRouter();

  return (
    <div className="w-full text-left">
      <FancyCheckbox value={brand.id} register={methods.register(name)}>
        {localeCore.getLocaleValueFallback(brand.name, locale!)} ({brand.code})
      </FancyCheckbox>
    </div>
  );
}
